/**
 * statisticsService.ts
 *
 * Wrapper sottile sull'endpoint /api/Request/aggregates: mappa la response
 * RequestAggregates alla shape legacy StatisticsData consumata da StatisticsPage.
 * I campi non più disponibili nello scope corrente (byUser, taskStats, overdue)
 * sono restituiti vuoti / a zero finché non saranno implementati lato BE.
 */

import { requestsNew } from './api';
import {
  StatisticsData,
  StatisticsFilters,
  StatisticsSummaryTypes,
  ClientStatistics,
  RequestTypeStatistics,
  TimeSeriesData,
} from '../types/statistics-types';
import { RequestType } from '../types/api';

export const statisticsService = {
  getStatistics: async (filters: StatisticsFilters = {}): Promise<StatisticsData> => {
    const aggregates = await requestsNew.getAggregates({
      type: filters.requestType as unknown as RequestType | undefined,
      startDate: filters.startDate ? filters.startDate.toISOString() : undefined,
      endDate: filters.endDate ? filters.endDate.toISOString() : undefined,
    });

    const byTypeCounts = aggregates.byType ?? ({} as Record<RequestType, number>);
    const totalRequests = aggregates.totalCount ?? 0;
    const completedRequests = aggregates.byTypeAndStatus
      .filter((b) => Number(b.status) === 4)
      .reduce((acc, b) => acc + b.count, 0);

    const summary: StatisticsSummaryTypes = {
      totalRequests,
      completedRequests,
      pendingRequests: Math.max(totalRequests - completedRequests, 0),
      overdueRequests: 0,
      avgCompletionTime: aggregates.completion?.overallAverageDays ?? 0,
      totalTasks: 0,
      completedTasks: 0,
      inProgressTasks: 0,
      overdueTasks: 0,
    };

    const byRequestType: RequestTypeStatistics[] = Object.entries(byTypeCounts).map(
      ([type, count]) => ({
        type: Number(type) as unknown as RequestTypeStatistics['type'],
        count: count as number,
        percentage: totalRequests > 0 ? ((count as number) / totalRequests) * 100 : 0,
      })
    );

    const byClient: ClientStatistics[] = (aggregates.topClients ?? []).map((c) => ({
      clientId: c.clientId != null ? String(c.clientId) : c.clientName,
      name: c.clientName,
      totalRequests: c.count,
      completedRequests: 0,
      pendingRequests: 0,
      avgDeliveryTime: 0,
    }));

    // Time series: aggrega monthlyTrend in totale per mese (newRequests).
    // I campi di task (overdueTasks, newTasks, completedTasks) sono 0 in questa fase.
    const monthMap = new Map<string, number>();
    (aggregates.monthlyTrend ?? []).forEach((b) => {
      const key = `${b.year}-${String(b.month).padStart(2, '0')}-01`;
      monthMap.set(key, (monthMap.get(key) ?? 0) + b.count);
    });
    const timeSeries: TimeSeriesData[] = Array.from(monthMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, newRequests]) => ({
        date,
        newRequests,
        completedRequests: 0,
        overdueTasks: 0,
        newTasks: 0,
        completedTasks: 0,
      }));

    return {
      summary,
      byUser: [],
      byClient,
      byRequestType,
      timeSeries,
    };
  },

  /**
   * @deprecated Endpoint di export non ancora disponibile lato BE.
   * Ritorna un blob placeholder; i bottoni Export sono nascosti in StatisticsPage.
   */
  exportStatisticsReport: async (
    format: 'pdf' | 'xlsx',
    _filters: StatisticsFilters = {}
  ): Promise<Blob> => {
    return new Blob([`Export ${format} non ancora disponibile`], {
      type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  },
};
